import { zodResolver } from '@hookform/resolvers/zod';
import { Netmask } from 'netmask';
import { useForm, useWatch } from 'react-hook-form';
import { Banner } from '#components/atoms/banner/Banner.tsx';
import { Checkbox } from '#components/atoms/forms/checkbox/Checkbox.tsx';
import { ErrorMessage, Input } from '#components/atoms/forms/index.ts';
import { Field } from '#components/patterns/forms/field/Field.tsx';
import { NetworkSize } from '#components/patterns/network/NetworkSize.tsx';
import { Spacer } from '#components/patterns/spacer/Spacer.tsx';
import { cidrRequiredSchema } from '#schemas/general/cidr.ts';
import { isPrivate } from '#schemas/general/helpers/ip-address.ts';
import {
  routableCIDRFormSchema,
  type RoutableCIDRFormData,
} from '#screens/routes/form/routable-cidr/routable-cidr-schema.ts';

const DEFAULT_VALUES = {
  cidr: '',
  install: true,
};

type Props = {
  onSave: (cidrConfig: RoutableCIDRFormData) => void;
  defaultValues?: RoutableCIDRFormData;
};

export function RoutableCIDRForm({ onSave, defaultValues }: Props) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
  } = useForm({
    resolver: zodResolver(routableCIDRFormSchema),
    defaultValues: defaultValues ?? DEFAULT_VALUES,
  });

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    return handleSubmit((data) => {
      return Promise.resolve(onSave(data));
    })(e);
  };

  const [currentCIDR, currentAutoInstallState] = useWatch({ control, name: ['cidr', 'install'] });
  const currentNetmask = cidrRequiredSchema.safeParse(currentCIDR).success ? new Netmask(currentCIDR) : undefined;

  return (
    <form action="post" id="add-routable-cidr" onSubmit={onSubmit}>
      <Spacer direction="vertical" size="wide">
        {currentAutoInstallState === true && currentNetmask && !isPrivate(currentNetmask) ?
          <Banner>
            Warning: Automatically installing a public CIDR route may result in cutting off access to the Managed Nebula
            service, preventing configuration updates.
          </Banner>
        : null}
        <Field label="Subnet">
          <Input className="width-100" {...register('cidr')} />
          {errors.cidr ?
            <ErrorMessage>{errors.cidr.message}</ErrorMessage>
          : cidrRequiredSchema.safeParse(currentCIDR).success ?
            <NetworkSize cidr={currentCIDR} reservedAddressCount={0} />
          : null}
        </Field>
        <Checkbox {...register('install')} label="Automatically install" />
      </Spacer>
    </form>
  );
}
